import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import React from 'react';
import AddressFields from 'react/shared/components/true_link/main/form/address_input/AddressFields';
import TrueLinkCheckboxes from 'react/shared/components/true_link/main/form/checkboxes/TrueLinkCheckboxes';
import TrueLinkRadios from 'react/shared/components/true_link/main/form/radios/TrueLinkRadios';
import TrueLinkTextInput from 'react/shared/components/true_link/main/form/text_input/TrueLinkTextInput';
import CardText from 'react/shared/components/true_link/main/form/wizard/CardText';
import PALETTE from 'react/shared/theme/palette';
import { oneLineAddress } from 'react/shared/utils/Address';
import ErrorText from 'react/signups/card/common/ErrorText';
import addressShape from 'react/signups/card/shapes/AddressShape';
import trustDetailsShape from 'react/signups/card/shapes/TrustDetails';

const useStyles = makeStyles({
  label: {
    color: '#000',
  },
  select: {
    height: 'auto',
    width: 'auto',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
  },
  subtitle: {
    color: PALETTE.grey2,
    marginTop: '-10px',
  },
  checkbox: {
    '& label': {
      border: 'none',
    },
  },
});

export default function TrustDetailsForm({
  onChange,
  setFieldTouched,
  setFieldValue,
  setValues,
  values,
  organizationName,
  organizationAddress,
}) {
  const classes = useStyles();
  const formattedAddress = oneLineAddress(organizationAddress);

  const handleBusinessIsTrusteeChange = () => {
    setFieldValue('trusteeName', '');
    setFieldValue('address', {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
    });
    setFieldValue('businessIsTrustee', !values.businessIsTrustee);
  };

  const handleTitleIncludedChange = (_e, val) => {
    setFieldValue('trusteeTitle', '');
    setFieldValue('titleIncludedInList', val);
  };

  const businessIsTrusteeOptions = [
    {
      label: `The trustee is ${organizationName}, ${formattedAddress}`,
      value: 'true',
    },
  ];

  const genericOptions = [
    {
      label: 'Yes',
      value: 'yes',
    },
    {
      label: 'No',
      value: 'no',
    },
  ];

  const trusteeCategoryOptions = [
    {
      label: 'Individual or sole proprietorship',
      value: 'An individual or sole proprietorship',
    },
    {
      label: 'Non-profit',
      value: 'Non-profit',
    },
    {
      label: 'Bank or credit union',
      value: 'Bank or credit union',
    },
    {
      label: 'Trust company',
      value: 'Trust company',
    },
    {
      label: 'Other for-profit entity',
      value: 'Other for-profit entity',
    },
  ];

  return (
    <>
      <CardText>We are required to gather this information.</CardText>
      <FormControl>
        <FormLabel className={classes.label} htmlFor="trusteeCategory">
          <Typography variant="body1">The trustee is a:</Typography>
        </FormLabel>
        <select
          className={classes.select}
          id="trusteeCategory"
          name="trusteeCategory"
          onChange={onChange}
          placeholder="Select an option"
          required
          value={values.trusteeCategory}
        >
          <option key="no-data" value="" />
          {trusteeCategoryOptions.map((trusteeCategory) => (
            <option key={trusteeCategory.label} value={trusteeCategory.value}>
              {trusteeCategory.label}
            </option>
          ))}
        </select>
        <ErrorText name="trusteeCategory" />
      </FormControl>

      {values.trusteeCategory !== 'An individual or sole proprietorship' &&
        values.trusteeCategory !== '' && (
          <div className={classes.checkbox}>
            <TrueLinkCheckboxes
              ariaLabel="The trustee is the business"
              name="businessIsTrustee"
              onChange={handleBusinessIsTrusteeChange}
              options={businessIsTrusteeOptions}
              value={values.businessIsTrustee ? ['true'] : []}
            />
          </div>
        )}
      <TrueLinkTextInput
        labelText="What is the name of the trust? "
        name="trustName"
        onChange={onChange}
        placeholder="Trust name"
        required={false}
        setFieldTouched={setFieldTouched}
        value={values.trustName}
      />
      {values.trusteeCategory !== 'An individual or sole proprietorship' &&
        values.businessIsTrustee === false && (
          <>
            <TrueLinkTextInput
              labelText="Trustee name"
              name="trusteeName"
              onChange={onChange}
              placeholder="Enter name"
              required={false}
              setFieldTouched={setFieldTouched}
              value={values.trusteeName}
            />
            <AddressFields
              labelPrefix="Business"
              onChange={onChange}
              setFieldTouched={setFieldTouched}
              setValues={setValues}
              values={values}
            />
          </>
        )}
      {values.trusteeCategory !== 'An individual or sole proprietorship' &&
        values.trusteeCategory !== '' && (
          <>
            <CardText>Do you have one of these titles or a role with similar authority?</CardText>
            <CardText className={classes.subtitle}>
              Trust Officer, Owner / Manager, Executive Director, Treasurer, Vice President,
              President, General Partner, Managing Member, CFO, COO, CEO.
            </CardText>
            <TrueLinkRadios
              ariaLabel="Is your title included in this list of titles"
              name="titleIncludedInList"
              onChange={handleTitleIncludedChange}
              options={genericOptions}
              value={values.titleIncludedInList}
            />
            <ErrorText name="titleIncludedInList" />
          </>
        )}
      {values.trusteeCategory !== 'An individual or sole proprietorship' &&
        values.titleIncludedInList === 'no' && (
          <TrueLinkTextInput
            labelText="Please enter your title"
            name="trusteeTitle"
            onChange={onChange}
            placeholder="Title"
            required={false}
            setFieldTouched={setFieldTouched}
            value={values.trusteeTitle}
          />
        )}
      {values.trusteeCategory === 'An individual or sole proprietorship' && (
        <TrueLinkTextInput
          labelText="Please enter the name of the individual or sole proprietorship, exactly as it is listed in the trust document."
          name="trusteeName"
          onChange={onChange}
          placeholder="Enter name"
          required={false}
          setFieldTouched={setFieldTouched}
          value={values.trusteeName}
        />
      )}
    </>
  );
}

TrustDetailsForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  values: trustDetailsShape.isRequired,
  organizationName: PropTypes.string,
  organizationAddress: addressShape,
};
